import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import Swal from "sweetalert2";
import { fetchAccountHeads } from "../../redux/slices/accountHeadSlice";
import { fetchBanks } from "../../redux/slices/bankSlice";
import { createIncomeEntryThunk, updateIncomeEntryThunk } from "../../redux/slices/financeSlice";
import { FINANCE_PAYMENT_MODES as PAYMENT_MODES } from "../../constants/paymentModes";
import { getApiErrorMessage } from "../../utils/apiError";

const today = () => new Date().toISOString().split("T")[0];

const EMPTY = {
  incomeDate: today(),
  accountHeadId: "",
  amount: "",
  paymentMode: "Cash",
  bankId: "",
  referenceNo: "",
  remarks: "",
};

const IncomeModal = ({ show, onClose, editRecord }) => {
  const dispatch = useDispatch();
  const accountHeads = useSelector((s) => s.accountHeads.list);
  const banks = useSelector((s) => s.bank.list);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const headOptions = accountHeads
    .filter((h) => h.status === "Active")
    .map((h) => ({ value: h.accountID || h.id, label: h.accountName }));
  const bankOptions = banks.map((b) => ({ value: b.bankID || b.id, label: b.bankName }));

  useEffect(() => {
    dispatch(fetchAccountHeads());
    dispatch(fetchBanks());
  }, [dispatch]);

  useEffect(() => {
    // The API returns PascalCase-ID field names (accountHeadID, paymentType, bankID,
    // incomeEntryNo) that don't match this form's camelCase state keys — a naive
    // {...EMPTY, ...editRecord} spread left Payment Mode stuck on "Cash" (and Account
    // Head/Bank blank) whenever editing an entry that was actually paid via Bank/UPI.
    setForm(editRecord ? {
      incomeDate: (editRecord.incomeDate || today()).split("T")[0],
      accountHeadId: editRecord.accountHeadID ?? editRecord.accountHeadId ?? "",
      amount: editRecord.amount ?? "",
      paymentMode: editRecord.paymentType ?? editRecord.paymentMode ?? "Cash",
      bankId: editRecord.bankID ?? editRecord.bankId ?? "",
      referenceNo: editRecord.incomeEntryNo ?? editRecord.referenceNo ?? "",
      remarks: editRecord.remarks || "",
    } : { ...EMPTY, incomeDate: today() });
  }, [editRecord, show]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.accountHeadId || !form.amount || !form.incomeDate) {
      Swal.fire("Required", "Account Head, Date and Amount are required.", "warning");
      return;
    }
    setSaving(true);
    try {
      // Raw API records key off incomeID, never a bare `id` — checking editRecord?.id
      // was always false, so "Save Changes" silently created a new entry instead of
      // updating the one being edited.
      const recordId = editRecord?.incomeID ?? editRecord?.id;
      if (recordId) {
        await dispatch(updateIncomeEntryThunk({ id: recordId, data: form })).unwrap();
      } else {
        await dispatch(createIncomeEntryThunk(form)).unwrap();
      }
      Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Income entry saved", showConfirmButton: false, timer: 2000 });
      onClose();
    } catch (err) {
      Swal.fire("Error", getApiErrorMessage(err, "Failed to save income entry."), "error");
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header border-0 custom-modal-header">
              <div className="page-title">
                <h4>{editRecord ? "Edit Income Entry" : "Add Income Entry"}</h4>
              </div>
              <button type="button" className="close" onClick={onClose}><span>×</span></button>
            </div>
            <div className="modal-body custom-modal-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-lg-6 mb-3">
                    <label className="form-label">Date <span className="text-danger">*</span></label>
                    <input type="date" className="form-control" value={form.incomeDate} onChange={set("incomeDate")} />
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label className="form-label">Amount (₹) <span className="text-danger">*</span></label>
                    <input type="number" className="form-control" min={0} step="0.01" value={form.amount} onChange={set("amount")} placeholder="0.00" />
                  </div>
                  <div className="col-lg-12 mb-3">
                    <label className="form-label">Account Head <span className="text-danger">*</span></label>
                    <Select
                      classNamePrefix="react-select"
                      options={headOptions}
                      value={headOptions.find((o) => o.value === form.accountHeadId) || null}
                      onChange={(opt) => setForm((f) => ({ ...f, accountHeadId: opt?.value || "" }))}
                      placeholder="Select account head"
                      menuPosition="fixed"
                      styles={{ menu: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label className="form-label">Payment Mode</label>
                    <Select
                      classNamePrefix="react-select"
                      options={PAYMENT_MODES}
                      value={PAYMENT_MODES.find((o) => o.value === form.paymentMode) || null}
                      onChange={(opt) => setForm((f) => ({ ...f, paymentMode: opt?.value || "Cash" }))}
                      menuPosition="fixed"
                      styles={{ menu: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                  </div>
                  {form.paymentMode !== "Cash" && (
                    <div className="col-lg-6 mb-3">
                      <label className="form-label">Bank</label>
                      <Select
                        classNamePrefix="react-select"
                        options={bankOptions}
                        value={bankOptions.find((o) => o.value === form.bankId) || null}
                        onChange={(opt) => setForm((f) => ({ ...f, bankId: opt?.value || "" }))}
                        placeholder="Select bank"
                        isClearable
                        menuPosition="fixed"
                        styles={{ menu: (base) => ({ ...base, zIndex: 9999 }) }}
                      />
                    </div>
                  )}
                  <div className="col-lg-6 mb-3">
                    <label className="form-label">Reference No.</label>
                    <input className="form-control" value={form.referenceNo} onChange={set("referenceNo")} placeholder="UTR / Txn Id" />
                  </div>
                  <div className="col-lg-12 mb-3">
                    <label className="form-label">Remarks</label>
                    <textarea className="form-control" rows={2} value={form.remarks} onChange={set("remarks")} placeholder="Brief description" />
                  </div>
                </div>
                <div className="modal-footer-btn">
                  <button type="button" className="btn btn-cancel me-2" onClick={onClose}>Cancel</button>
                  <button type="submit" className="btn btn-submit" disabled={saving}>
                    {saving ? "Saving..." : editRecord ? "Save Changes" : "Add Income"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={onClose} />
    </>
  );
};

export default IncomeModal;
